import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import TrackerLayout from '../components/TrackerLayout';
import { EditLoggedClassLink } from '../components/ClassLogEdit';
import TaskRows, { emptyTask, tasksToSend } from '../components/TaskRows';
import { useMentorSession } from '../context/TrackerContext';
import {
  getMentorStudents,
  getMentors,
  submitClass,
} from '../lib/api';
import type { MentorInfo, StudentBasic, TaskItem } from '../lib/api';

// ==================== HELPERS ================================

const todayStr = () => new Date().toISOString().slice(0, 10);

type AbsentType = 'Informed' | 'NotInformed' | 'Leave';

const EMPTY_FORM = {
  studentIds: [] as string[],
  date: todayStr(),
  attendance: 'Present' as 'Present' | 'Absent',
  absentType: 'NotInformed' as AbsentType,
  portion: '',
  topic: '',
  startTime: '',
  endTime: '',
  remark: '',
  takenByMentorId: '',
};

// ==================== LOG CLASS TAB ==========================

const LogClassTab: React.FC<{
  mentorId: string;
  students: StudentBasic[];
  allMentors: MentorInfo[];
}> = ({ mentorId, students, allMentors }) => {
  const [form, setForm] = useState({ ...EMPTY_FORM, takenByMentorId: mentorId });
  // The tasks given in this class: each ticked student gets each one
  const [tasks, setTasks] = useState<TaskItem[]>([emptyTask()]);
  const [loading, setLoading]     = useState(false);
  const [progress, setProgress]   = useState('');
  const [studentSearch, setStudentSearch] = useState('');

  // "Covering for" state
  const [coveringMentorId, setCoveringMentorId] = useState('');
  const [coverStudents, setCoverStudents]         = useState<StudentBasic[]>([]);
  const [loadingCover, setLoadingCover]           = useState(false);

  const activeStudents = coveringMentorId ? coverStudents : students;

  const filteredStudents = studentSearch
    ? activeStudents.filter(s =>
        s.name.toLowerCase().includes(studentSearch.toLowerCase()) ||
        s.studentId.toLowerCase().includes(studentSearch.toLowerCase()))
    : activeStudents;

  const handleCoveringChange = async (selectedMentorId: string) => {
    setCoveringMentorId(selectedMentorId);
    setForm(prev => ({ ...prev, studentIds: [] }));
    if (!selectedMentorId) { setCoverStudents([]); return; }
    setLoadingCover(true);
    try {
      const res = await getMentorStudents(selectedMentorId);
      setCoverStudents(res.students);
    } catch (err: any) {
      toast.error('Could not load students: ' + err.message);
    } finally {
      setLoadingCover(false);
    }
  };

  const set = (field: string, value: string) =>
    setForm(prev => ({ ...prev, [field]: value }));

  const toggleStudent = (id: string) => {
    setForm(prev => {
      const ids = prev.studentIds.includes(id)
        ? prev.studentIds.filter(x => x !== id)
        : [...prev.studentIds, id];
      return { ...prev, studentIds: ids };
    });
  };

  const toggleAllStudents = () => {
    setForm(prev => ({
      ...prev,
      studentIds: prev.studentIds.length === filteredStudents.length
        ? []
        : filteredStudents.map(s => s.studentId),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (form.studentIds.length === 0) { toast.error('Select at least one student'); return; }
    if (!form.takenByMentorId) { toast.error('Select who took the class'); return; }
    const given = form.attendance === 'Absent' ? [] : tasksToSend(tasks);
    if (given.some(t => !t.title)) { toast.error('Give each task a title'); return; }
    setLoading(true);
    const toSubmit = activeStudents.filter(s => form.studentIds.includes(s.studentId));
    let done = 0; let failed = 0; let firstError = '';
    for (const s of toSubmit) {
      setProgress(`Logging ${s.name}… (${done + 1}/${toSubmit.length})`);
      try {
        await submitClass({
          studentId: s.studentId,
          takenByMentorId: form.takenByMentorId,
          date: form.date,
          attendance: form.attendance,
          absentType: form.attendance === 'Absent' ? form.absentType : undefined,
          sendAbsentEmail: form.attendance === 'Absent' && form.absentType !== 'Leave',
          portion: form.portion,
          topic: form.topic,
          startTime: form.startTime,
          endTime: form.endTime,
          completionStatus: 'Task Given',
          tasks: given,
          remark: form.remark,
        });
        done++;
      } catch (err: any) {
        failed++;
        firstError = firstError || err.message;
      }
    }
    setLoading(false);
    setProgress('');
    if (done > 0) toast.success(`Class logged for ${done} student${done !== 1 ? 's' : ''}!${given.length ? ` ${given.length} task${given.length === 1 ? '' : 's'} sent to each.` : ''}`);
    if (failed > 0) toast.error(`${failed} submission${failed !== 1 ? 's' : ''} failed${firstError ? `: ${firstError}` : ''}`);
    if (failed > 0 && done === 0) return;
    setForm({ ...EMPTY_FORM, takenByMentorId: mentorId, date: todayStr() });
    setTasks([emptyTask()]);
    setStudentSearch('');
  };

  const isAbsent = form.attendance === 'Absent';

  if (students.length === 0) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-center text-amber-800 text-sm">
        No active students assigned to you. Ask the admin to assign students.
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 space-y-5">

      {/* Covering for another mentor */}
      <div className="rounded-xl border-2 border-dashed border-gray-200 p-3 space-y-2">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Covering for another mentor?</span>
          <span className="text-xs text-gray-400">(optional)</span>
        </div>
        <select value={coveringMentorId} onChange={e => handleCoveringChange(e.target.value)} className="input-base">
          <option value="">No — logging for my own students</option>
          {allMentors.filter(m => m.mentorId !== mentorId).map(m => (
            <option key={m.mentorId} value={m.mentorId}>Covering for {m.mentorName}</option>
          ))}
        </select>
        {coveringMentorId && (
          <p className="text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-1.5">
            Showing <strong>{allMentors.find(m => m.mentorId === coveringMentorId)?.mentorName}'s</strong> students.
            The Roster assignment is unchanged — this is a one-day cover only.
          </p>
        )}
      </div>

      {/* Students multi-select */}
      <Field label={`Students * ${form.studentIds.length > 0 ? `(${form.studentIds.length} selected)` : ''}`}>
        {loadingCover ? (
          <div className="text-sm text-gray-400 py-3">Loading students…</div>
        ) : (
          <div className="border-2 border-gray-200 rounded-xl overflow-hidden focus-within:border-[#2b2240]">
            {/* Search + select-all bar */}
            <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100 bg-gray-50">
              <svg className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" fill="none">
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
              </svg>
              <input type="text" placeholder="Search students…" value={studentSearch}
                onChange={e => setStudentSearch(e.target.value)}
                className="flex-1 text-xs bg-transparent outline-none" />
              <button type="button" onClick={toggleAllStudents}
                className="text-[10px] font-bold px-2 py-0.5 rounded border border-gray-200 text-gray-500 hover:bg-white whitespace-nowrap flex-shrink-0">
                {form.studentIds.length === filteredStudents.length && filteredStudents.length > 0 ? 'Deselect All' : 'Select All'}
              </button>
            </div>
            {/* Checklist */}
            <div className="divide-y divide-gray-100 max-h-48 overflow-y-auto">
              {filteredStudents.length === 0 ? (
                <p className="text-xs text-gray-400 text-center py-4">No students found</p>
              ) : filteredStudents.map(s => {
                const checked = form.studentIds.includes(s.studentId);
                return (
                  <label key={s.studentId}
                    className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer select-none transition-colors ${checked ? 'bg-purple-50' : 'hover:bg-gray-50'}`}>
                    <input type="checkbox" checked={checked} onChange={() => toggleStudent(s.studentId)}
                      className="w-4 h-4 accent-purple-600 flex-shrink-0" />
                    <span className="text-sm font-medium flex-1 truncate" style={{ color: checked ? '#5b21b6' : '#374151' }}>{s.name}</span>
                    <span className="text-[10px] text-gray-400 font-mono flex-shrink-0">{s.studentId}</span>
                    {checked && <span className="text-purple-500 text-xs flex-shrink-0">✓</span>}
                  </label>
                );
              })}
            </div>
          </div>
        )}
      </Field>

      {/* Date */}
      <Field label="Date *">
        <input type="date" value={form.date} onChange={e => set('date', e.target.value)} required className="input-base" />
      </Field>

      {/* Attendance toggle */}
      <Field label="Attendance *">
        <div className="flex gap-3">
          {(['Present', 'Absent'] as const).map(opt => (
            <button key={opt} type="button" onClick={() => set('attendance', opt)}
              className="flex-1 py-2.5 rounded-lg text-sm font-semibold border-2 transition-all"
              style={form.attendance === opt
                ? opt === 'Present'
                  ? { background: '#16a34a', color: '#fff', borderColor: '#16a34a' }
                  : { background: '#dc2626', color: '#fff', borderColor: '#dc2626' }
                : { background: '#f9fafb', color: '#374151', borderColor: '#e5e7eb' }}>
              {opt}
            </button>
          ))}
        </div>
      </Field>

      {/* Absent reason — only shown when Absent */}
      {isAbsent && (
        <Field label="Absence reason *">
          <div className="flex gap-2 flex-wrap">
            {([
              { value: 'Informed',    label: 'Informed',             activeStyle: { background: '#d97706', color: '#fff', borderColor: '#d97706' } },
              { value: 'NotInformed', label: 'Not Informed',          activeStyle: { background: '#dc2626', color: '#fff', borderColor: '#dc2626' } },
              { value: 'Leave',       label: 'Leave (Institute)',      activeStyle: { background: '#2563eb', color: '#fff', borderColor: '#2563eb' } },
            ] as { value: AbsentType; label: string; activeStyle: React.CSSProperties }[]).map(opt => (
              <button
                key={opt.value}
                type="button"
                onClick={() => set('absentType', opt.value)}
                className="flex-1 py-2.5 rounded-lg text-sm font-semibold border-2 transition-all"
                style={form.absentType === opt.value ? opt.activeStyle : { background: '#f9fafb', color: '#374151', borderColor: '#e5e7eb' }}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {/* Auto email info */}
          {form.absentType !== 'Leave' ? (
            <div className="mt-2 flex items-center gap-2 px-3 py-2 rounded-lg text-xs"
              style={{ background: form.absentType === 'Informed' ? '#fffbeb' : '#fef2f2',
                       color:  form.absentType === 'Informed' ? '#92400e' : '#991b1b' }}>
              <svg className="w-3.5 h-3.5 flex-shrink-0" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" fill="none">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
              </svg>
              {form.absentType === 'Informed'
                ? 'A polite acknowledgement email will be sent to the student automatically.'
                : 'A formal warning email will be sent to the student automatically.'}
            </div>
          ) : (
            <p className="text-xs text-blue-600 mt-2 px-1">No notification email will be sent for leave absences.</p>
          )}
        </Field>
      )}

      {/* Portion + Topic */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label={`Portion Covered${isAbsent ? ' (optional)' : ' *'}`}>
          <input type="text" value={form.portion} onChange={e => set('portion', e.target.value)}
            required={!isAbsent} disabled={isAbsent} placeholder="e.g. Chapter 3 — Loops"
            className="input-base disabled:bg-gray-50 disabled:text-gray-400" />
        </Field>
        <Field label={`Topic Covered${isAbsent ? ' (optional)' : ' *'}`}>
          <input type="text" value={form.topic} onChange={e => set('topic', e.target.value)}
            required={!isAbsent} disabled={isAbsent} placeholder="e.g. SQL Joins, Python Functions…"
            className="input-base disabled:bg-gray-50 disabled:text-gray-400" />
        </Field>
      </div>

      {/* Times */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Start Time">
          <input type="time" value={form.startTime} onChange={e => set('startTime', e.target.value)}
            disabled={isAbsent} className="input-base disabled:bg-gray-50 disabled:text-gray-400" />
        </Field>
        <Field label="End Time">
          <input type="time" value={form.endTime} onChange={e => set('endTime', e.target.value)}
            disabled={isAbsent} className="input-base disabled:bg-gray-50 disabled:text-gray-400" />
        </Field>
      </div>

      {/* Tasks given (only for present sessions): each goes to the students
          ticked, who send it from My Tasks; you review it in Tasks & Reviews */}
      {!isAbsent && (
        <div className="space-y-2">
          <Field label="Tasks given in this class">
            <TaskRows rows={tasks} onChange={setTasks} minDate={form.date} />
          </Field>
          <p className="text-[11px] text-gray-500 px-1">
            The students see each task in <strong>My Tasks</strong> (website and app) and send their work there. You review it in <strong>Tasks &amp; Reviews</strong>. No task given? Leave the rows empty.
          </p>
        </div>
      )}

      {/* Taken By */}
      <Field label="Taken By (Mentor) *">
        <select value={form.takenByMentorId} onChange={e => set('takenByMentorId', e.target.value)} required className="input-base">
          <option value="">— select mentor —</option>
          {allMentors.map(m => (
            <option key={m.mentorId} value={m.mentorId}>
              {m.mentorName}{m.mentorId === mentorId ? ' (you)' : ''}
            </option>
          ))}
        </select>
      </Field>

      {/* Remark */}
      <Field label="Remark (optional)">
        <textarea value={form.remark} onChange={e => set('remark', e.target.value)}
          rows={2} placeholder="Any notes about this session…" className="input-base resize-none" />
      </Field>

      <button type="submit" disabled={loading}
        className="w-full py-3 rounded-xl font-bold text-sm transition-all shadow-md hover:shadow-lg hover:scale-[1.01] disabled:opacity-60 disabled:cursor-not-allowed disabled:scale-100"
        style={{ background: loading ? '#9ca3af' : 'linear-gradient(135deg,#312741,#5a4a6f)', color: '#fff' }}>
        {loading ? (
          <span className="flex items-center justify-center gap-2">
            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            {progress || 'Submitting…'}
          </span>
        ) : `Submit Class Record${form.studentIds.length > 1 ? ` for ${form.studentIds.length} Students` : ''}`}
      </button>
    </form>
  );
};

// ==================== MAIN PAGE ==============================

const MentorForm: React.FC = () => {
  const { mentorId, mentorName } = useMentorSession();
  const [students, setStudents] = useState<StudentBasic[]>([]);
  const [allMentors, setAllMentors] = useState<MentorInfo[]>([]);
  const [fetching, setFetching] = useState(true);

  const load = useCallback(async () => {
    setFetching(true);
    try {
      const [sRes, mRes] = await Promise.all([getMentorStudents(mentorId), getMentors()]);
      setStudents(sRes.students);
      setAllMentors(mRes.mentors);
    } catch (err: any) {
      toast.error('Failed to load data: ' + err.message);
    } finally {
      setFetching(false);
    }
  }, [mentorId]);

  useEffect(() => { load(); }, [load]);

  return (
    <TrackerLayout>
      <div className="max-w-2xl mx-auto">
        <div className="mb-5">
          <h1 className="text-2xl font-bold" style={{ color: '#2b2240' }}>Log a Class</h1>
          <p className="text-gray-500 text-sm mt-0.5">
            Record today's session &nbsp;&middot;&nbsp;
            <span className="font-medium text-gray-700">{mentorName}</span>
          </p>
        </div>

        <EditLoggedClassLink to="/tracker/mentor/edit-logs" />

        {fetching ? (
          <div className="flex items-center justify-center h-40 text-gray-400">Loading…</div>
        ) : (
          <LogClassTab mentorId={mentorId} students={students} allMentors={allMentors} />
        )}
      </div>

      <style>{`
        .input-base { width:100%; padding:0.625rem 0.875rem; border:2px solid #e5e7eb; border-radius:0.5rem; font-size:0.875rem; color:#111827; transition:border-color .15s; outline:none; background:#fff; }
        .input-base:focus { border-color:#2b2240; }
        .input-base:disabled { cursor:not-allowed; }
      `}</style>
    </TrackerLayout>
  );
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">{label}</label>
    {children}
  </div>
);

export default MentorForm;
